import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBed, faSearch, faSpinner, faTimes, faTrophy, faCrown, faLayerGroup, faFire, faCalendar } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';
import '../styles/ItemBar.css';
import '../styles/ItemReward.css';

interface AwardedItem {
  item_def_id: number;
  name: string;
  icon: string;
  description?: string;
  effect_value?: number;
}

interface PendingReward {
  id: number;
  choices_count: number;
  reveal_data?: { awarded_items: AwardedItem[] };
  combined_points?: number;
  player_a_points?: number;
  player_b_points?: number;
  player_a_name?: string;
  player_a_avatar?: string;
  player_b_name?: string;
  player_b_avatar?: string;
}

interface ItemRewardModalProps {
  onClaimed: () => void;
  onDismiss: () => void;
}

const ICON_MAP: Record<string, any> = { 'fa-bed': faBed, 'fa-search': faSearch, 'fa-trophy': faTrophy, 'fa-crown': faCrown, 'fa-layer-group': faLayerGroup, 'fa-fire': faFire, 'fa-calendar': faCalendar };

export default function ItemRewardModal({ onClaimed, onDismiss }: ItemRewardModalProps) {
  const { token } = useAuth();
  const [reward, setReward] = useState<PendingReward | null>(null);
  const [loading, setLoading] = useState(true);
  const [claimed, setClaimed] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) return;
    api.get('/player/me/item-reward', token)
      .then((data: any) => { setReward(data?.reward ?? null); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);
  const [openTooltip, setOpenTooltip] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [barProgress, setBarProgress] = useState(0);
  const [showCards, setShowCards] = useState(false);

  // Animate the bar fill over 2 seconds, then show cards
  useEffect(() => {
    if (!reward?.combined_points) return;
    const targetPct = Math.min(100, reward.combined_points / 1000 * 100);
    const duration = 2000;
    const start = performance.now();
    let raf: number;
    const tick = (now: number) => {
      const elapsed = now - start;
      const t = Math.min(elapsed / duration, 1);
      // ease-out
      const eased = 1 - Math.pow(1 - t, 3);
      setBarProgress(eased * targetPct);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    const timer = setTimeout(() => setShowCards(true), duration);
    return () => { cancelAnimationFrame(raf); clearTimeout(timer); };
  }, [reward?.combined_points]);

  // Close tooltip on outside click
  useEffect(() => {
    if (openTooltip === null) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpenTooltip(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openTooltip]);

  const toggleTooltip = useCallback((itemId: number) => {
    setOpenTooltip(prev => prev === itemId ? null : itemId);
  }, []);

  const handleClaim = useCallback(async () => {
    if (!reward || !token || claimed || claiming) return;
    setClaiming(true);
    setError('');
    try {
      const result = await api.post('/player/me/item-reward/claim', {
        reward_id: reward.id, item_def_id: 0,
      }, token);
      if (!result?.ok) {
        setError(result?.error || result?.message || 'Could not claim this reward. Please try again.');
        return;
      }
      setClaimed(true);
      setTimeout(() => onClaimed(), 1200);
    } catch (claimError: any) {
      setError(claimError?.message || 'Could not claim this reward. Please try again.');
    } finally {
      setClaiming(false);
    }
  }, [reward, token, claimed, claiming, onClaimed]);

  const awardedItems: AwardedItem[] = reward?.reveal_data?.awarded_items ?? [];

  if (loading) return (
    <div className="item-reward-overlay">
      <div className="item-reward-modal"><FontAwesomeIcon icon={faSpinner} className="fa-spin item-reward-spinner" /></div>
    </div>
  );

  if (!reward) return (
    <div className="item-reward-overlay" onClick={onDismiss}>
      <div className="item-reward-modal" onClick={e => e.stopPropagation()}>
        <div className="item-reward-header">
          <span className="item-reward-title">Item Reward</span>
          <button className="item-reward-close" onClick={onDismiss}><FontAwesomeIcon icon={faTimes} /></button>
        </div>
        <div className="item-reward-body">
          <p className="item-reward-empty">No item reward available for this match.</p>
          <button className="item-reward-btn item-reward-btn--dismiss" onClick={onDismiss}>Continue</button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="item-reward-overlay" onClick={onDismiss}>
      <div className="item-reward-modal" onClick={e => e.stopPropagation()} ref={containerRef}>
        <div className="item-reward-header">
          <span className="item-reward-title">Item Reward</span>
        </div>
        <div className="item-reward-body">
          <p className="item-reward-subtitle">You received {awardedItems.length} new item{awardedItems.length !== 1 ? 's' : ''}!</p>

          {/* Combined Points Bar */}
          {reward.combined_points != null && (
            <div className="item-reward-points-bar">
              <div className="item-reward-points-bar-wrap">
                {/* Player A segment */}
                {reward.player_a_points != null && reward.combined_points > 0 && (
                  <div
                    className="item-reward-points-bar-segment item-reward-points-bar-segment--me"
                    style={{ '--target-width': `${(reward.player_a_points / Math.max(reward.combined_points, 1)) * 100}%` } as React.CSSProperties}
                  >
                    {reward.player_a_avatar && <img src={reward.player_a_avatar} alt="" className="item-reward-bar-avatar" />}
                    <span className="item-reward-bar-pts">{reward.player_a_points}P</span>
                  </div>
                )}
                {/* Player B segment */}
                {reward.player_b_points != null && reward.combined_points > 0 && (
                  <div
                    className="item-reward-points-bar-segment item-reward-points-bar-segment--opp"
                    style={{ '--target-width': `${(reward.player_b_points / Math.max(reward.combined_points, 1)) * 100}%` } as React.CSSProperties}
                  >
                    {reward.player_b_avatar && <img src={reward.player_b_avatar} alt="" className="item-reward-bar-avatar" />}
                    <span className="item-reward-bar-pts">{reward.player_b_points}P</span>
                  </div>
                )}
                {/* Threshold markers */}
                {[600, 1000].map(threshold => {
                  const pct = Math.min(98, (threshold / Math.max(reward.combined_points!, 1)) * 100);
                  if (pct > 100) return null;
                  const itemCount = threshold < 1000 ? 3 : 4;
                  return (
                    <div key={threshold} className="item-reward-threshold" style={{ left: `${pct}%` }}>
                      <div className="item-reward-threshold-line" />
                      <div className="item-reward-threshold-label">{itemCount} items</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Item Cards */}
          <div className={`item-reward-cards${showCards ? ' item-reward-cards--visible' : ''}`}>
            {awardedItems.map((item, idx) => {
              const iconDef = ICON_MAP[item.icon] ?? faBed;
              return (
                <div
                  key={`${item.item_def_id}-${idx}`}
                  className="item item--available"
                  onClick={() => toggleTooltip(item.item_def_id)}
                >
                  <div className="item-icon-wrap">
                    <FontAwesomeIcon icon={iconDef} className="item-icon" />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Tooltip */}
          {openTooltip !== null && (() => {
            const item = awardedItems.find(i => i.item_def_id === openTooltip);
            if (!item) return null;
            return (
              <div className="item-tooltip">
                <div className="item-tooltip-header">
                  <div>
                    <div className="item-tooltip-title">{item.name}</div>
                    <div className="item-tooltip-badge item-tooltip-badge--available">New Item</div>
                  </div>
                </div>
                {item.description && (
                  <>
                    <div className="item-tooltip-desc">{item.description}</div>
                    <div className="item-tooltip-divider" />
                  </>
                )}
                <div className="item-tooltip-condition">Item added to your inventory</div>
              </div>
            );
          })()}

          {error && <div className="item-reward-error">{error}</div>}

          <button className="item-reward-btn item-reward-btn--dismiss" onClick={handleClaim} disabled={claiming || claimed}>
            {claimed ? '✓ Claimed!' : claiming ? 'Saving...' : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
}
